<script setup lang="ts">
import { computed, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { isEmpty } from 'lodash';

import { emitCustomEvent } from '@/composables';
import { useYardInventory } from '@/composables/useYardInventory';
import { isTaskInProgress } from '@satellite/helpers/yardTaskUtils';
import { YardTaskStatus } from '@nova/core';

import type { CheckTaskViewData } from '@nova/core';

const props = defineProps<{
  task: CheckTaskViewData;
}>();

function isYardTaskStatus(value: string): value is YardTaskStatus {
  return (Object.values(YardTaskStatus) as string[]).includes(value);
}

const task = toRef(props, 'task');
const { t } = useI18n();

const taskId = computed(() => task.value.id ?? null);
const warehouseId = computed(() => task.value.warehouseId ?? null);

/**
 * Scope-wide asset counters surface under the OPEN INVENTORY button. The presenter is
 * `headless` because this title has no v-intersect host and is always visible while the task
 * detail dialog is open. Counts come straight from the BFF summary (OD-3625).
 */
const inventory = useYardInventory({
  taskId,
  warehouseId,
  headless: true
});

const counters = computed(() => inventory.warehouseCounts.value);

const miniCounters = computed(() => [
  {
    key: 'pending',
    icon: 'mdi-clipboard-outline',
    color: 'neutral-80',
    label: t('yardInventory.counters.pending'),
    count: counters.value.pending
  },
  {
    key: 'checked',
    icon: 'mdi-clipboard-check-outline',
    color: 'success',
    label: t('yardInventory.counters.checked'),
    count: counters.value.checked
  },
  {
    key: 'missing',
    icon: 'mdi-clipboard-remove-outline',
    color: 'error',
    label: t('yardInventory.counters.missing'),
    count: counters.value.missing
  }
]);

const isOpenInventoryDisabled = computed(
  () =>
    !isTaskInProgress(task.value) ||
    isEmpty(task.value.id) ||
    isEmpty(task.value.checkId) ||
    isEmpty(warehouseId.value)
);

function onOpenInventory() {
  const taskId = task.value.id;
  const checkId = task.value.checkId;
  const warehouse = warehouseId.value;
  const status = task.value.status;
  if (!taskId || !checkId || !warehouse || !isYardTaskStatus(status)) {
    return;
  }
  emitCustomEvent('open-check-spot-area-inventory', {
    taskId,
    checkId,
    // relatedId === warehouseId for inventory check tasks (OD-3622). The event
    // payload still carries it under `spotAreaId` for back-compat with the
    // panel listener; the panel only reads `warehouseId` from the payload.
    spotAreaId: warehouse,
    warehouseId: warehouse,
    taskStatus: status,
    checkAssetContainers: task.value.checkAssetContainers
  });
}
</script>

<template>
  <v-card variant="flat" border class="text-center" data-testid="check-task-details-title">
    <v-card-text class="pt-5">
      <div
        class="d-flex align-center justify-center gap-2"
        data-testid="check-task-details-title-inventory-count">
        <v-icon size="20" color="neutral-90" icon="mdi-truck-trailer" />
        <i18n-t keypath="yardInventory.assetCount" tag="span" class="typography-heading-lg">
          <template #count>
            <span class="typography-heading-lg-bold">{{ counters.total }}</span>
          </template>
        </i18n-t>
        <v-chip size="small" variant="flat" class="inventory-type-chip">
          {{ t('checkTaskDetails.entityChips.inventory') }}
        </v-chip>
      </div>

      <v-btn
        class="mt-6 mb-2 typography-button-lg"
        size="large"
        color="primary"
        :disabled="isOpenInventoryDisabled"
        append-icon="mdi-arrow-right"
        data-testid="check-task-open-inventory-btn"
        @click="onOpenInventory">
        {{ t('checkTaskDetails.buttons.openInventory') }}
      </v-btn>

      <v-divider class="my-4" />
      <div
        class="d-flex align-center justify-center gap-4 flex-wrap"
        data-testid="check-task-details-title-inventory-counters">
        <div
          v-for="counter in miniCounters"
          :key="counter.key"
          class="d-flex align-center gap-1 typography-body-sm">
          <v-icon size="16" :color="counter.color" :icon="counter.icon" />
          <span class="typography-body-sm-bold">{{ counter.count }}</span>
          <span>{{ counter.label }}</span>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<style scoped>
.inventory-type-chip {
  background-color: rgb(var(--v-theme-neutral-20));
  color: rgb(var(--v-theme-neutral-80));
  font-weight: 600;
}
</style>
